<template>
  <div class="data-request-selector">
    <label class="field-label">Data Request*</label>
    <v-select
      v-model="selectedRequest"
      :items="requestItems"
      placeholder="Select a Data Request List"
      variant="outlined"
      density="compact"
      hide-details
      item-title="displayTitle"
      item-value="id"
      class="data-request-select"
      @update:modelValue="handleRequestChange"
    >
      <!-- Custom item template with edit icons -->
      <template v-slot:item="{ item, props }">
        <v-list-item v-bind="props" class="request-item">
          <template v-slot:append v-if="item.raw.isCustom">
            <div class="item-actions">
              <v-btn
                size="small"
                variant="text"
                color="primary"
                @click.stop="editRequest(item.raw)"
                class="edit-btn"
              >
                <img src="/assets/edit.png" alt="Edit" class="edit-icon" />
              </v-btn>
            </div>
          </template>
        </v-list-item>
      </template>

      <!-- Fixed "Add Custom" button at bottom -->
      <template v-slot:append-item>
        <v-list-item
          class="add-custom-item"
          @click="openCreateDialog"
        >
          <template v-slot:prepend>
            <v-icon color="#000000">mdi-plus</v-icon>
          </template>
          <v-list-item-title class="add-custom-text">Add Custom</v-list-item-title>
        </v-list-item>
      </template>
    </v-select>
    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <!-- Create/Edit Dialog -->
    <BaseDialog
      :visible="showCreateDialog"
      :config="createDialogConfig"
      :isValidForSubmit="isValid"
      @close="closeCreateDialog"
      @action="saveRequest"
    >
      <p class="text-subtitle-2 mb-4">
        Create a custom data request list to add to the property contact template.
      </p>

      <!-- List Name Field -->
      <div class="dialog-field-container">
        <label class="dialog-field-label">List Name*</label>
        <v-text-field
          v-model="dialogData.name"
          placeholder="List Name"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
        ></v-text-field>
        <div class="field-hint" :class="{ 'error': dialogData.name && dialogData.name.length > 20 }">
          {{ dialogData.name ? dialogData.name.length : 0 }}/20 characters
        </div>
      </div>

      <!-- Data Request List Field -->
      <div class="dialog-field-container">
        <label class="dialog-field-label">Data Request List*</label>
        <div class="data-request-content-container">
          <VueEditor
            ref="dataRequestContentEditor"
            key="dataRequestContentEditor"
            class="data-request-content-vue-editor"
            v-model="dialogData.content"
            :editorToolbar="customToolbar"
            :editorOptions="editorOptions"
            placeholder="Additional Comments"
            @input="onDataRequestContentInput"
            @text-change="onDataRequestContentTextChange"
          />
        </div>
        
        <!-- Character Count -->
        <div class="data-request-content-char-count" :class="{ error: dataRequestContentLength > 1000 }">
          {{ dataRequestContentLength }}/1000 characters
        </div>
      </div>

      <!-- Delete button for editing -->
      <div v-if="isEditing" class="delete-section">
        <v-btn
          variant="outlined"
          color="#003f2d"
          @click="deleteCurrentRequest"
          :loading="isDeleting"
          class="delete"
        >
          <img src="/assets/delete.png" alt="Delete" class="delete-icon" />
          Delete
        </v-btn>
      </div>
    </BaseDialog>

    <!-- Delete Confirmation Dialog -->
    <BaseDialog
      :visible="showDeleteDialog"
      :config="deleteDialogConfig"
      :isValidForSubmit="true"
      @close="showDeleteDialog = false"
      @action="confirmDelete"
    >
      <p class="text-body-1 mb-3">
        Are you sure you want to delete "<strong>{{ requestToDelete?.name }}</strong>"?
      </p>
      <p class="text-body-2 text-medium-emphasis">
        This action cannot be undone.
      </p>
    </BaseDialog>
  </div>
</template>

<script>
import BaseDialog from '../commons/BaseDialog.vue';
import { VueEditor } from 'vue3-editor';

const STORAGE_KEY = 'customDataRequests';

export default {
  name: 'DataRequestSelector',
  
  components: {
    BaseDialog,
    VueEditor
  },
  
  props: {
    modelValue: {
      type: String,
      default: null
    },
    systemOptions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'request-changed', 'error', 'success'],
  data() {
    return {
      selectedRequest: null,
      error: '',
      customRequests: [],
      showCreateDialog: false,
      showDeleteDialog: false,
      isEditing: false,
      isSaving: false,
      isDeleting: false,
      
      dialogData: {
        id: null,
        name: '',
        content: ''
      },
      
      requestToDelete: null,
      
      // VueEditor configuration
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link'],
        ['clean']
      ],
      
      editorOptions: {
        debug: false,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
            ['clean']
          ],
          history: {
            delay: 1000,
            maxStack: 50,
            userOnly: false
          },
          clipboard: {
            matchVisual: false
          }
        },
        theme: 'snow',
        strict: false,
        scrollingContainer: null,
        bounds: null,
        readOnly: false,
      }
    };
  },
  computed: {
    requestItems() {
      const systemItems = (this.systemOptions || []).map(option => ({
        id: `system_${option}`,
        displayTitle: option,
        name: option,
        isCustom: false
      }));
      
      const customItems = this.customRequests.map(request => ({
        id: `custom_${request.id}`,
        displayTitle: request.name,
        name: request.name,
        isCustom: true,
        customId: request.id
      }));
      
      // Reverse custom items to show latest created at top, then system items
      return [...customItems.reverse(), ...systemItems];
    },
    
    isValid() {
      // Check name: must have actual text and not exceed length
      const nameText = String(this.dialogData.name || '').trim();
      if (!nameText || nameText.length > 20) return false;
      
      // Check content: must have actual text content (not just HTML markup) and not exceed limit
      const contentLength = this.dataRequestContentLength;
      if (contentLength <= 0 || contentLength > 1000) return false;
      
      return true;
    },
    
    dataRequestContentLength() {
      if (!this.dialogData.content) return 0;
      
      try {
        // Get plain text length (strip HTML tags)
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.dialogData.content || '';
        const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
        return textContent.length;
      } catch (error) {
        console.warn('Error calculating data request content length:', error);
        return 0;
      }
    },
    
    createDialogConfig() {
      return {
        title: 'Add Custom List',
        label: this.isEditing ? 'Save Changes' : 'Add List',
        needCanacel: true,
        hasIcon: false,
        isSelectAssignment: false
      };
    },
    
    deleteDialogConfig() {
      return {
        title: 'Confirm Delete',
        label: 'Delete',
        needCanacel: true,
        hasIcon: false,
        isSelectAssignment: false
      };
    }
  },
  watch: {
    modelValue: {
      handler(newValue) {
        // If newValue is a plain name (string), find the matching item ID
        if (newValue && typeof newValue === 'string') {
          const matchingItem = this.requestItems.find(item => item.name === newValue);
          if (matchingItem) {
            this.selectedRequest = matchingItem.id;
          } else {
            this.selectedRequest = newValue;
          }
        } else {
          this.selectedRequest = newValue;
        }
      },
      immediate: true
    },
    systemOptions: {
      handler(newOptions) {
        // Don't auto-select - let parent component control the selection via v-model
        // Only handle if options change and current selection becomes invalid
        if (this.selectedRequest && newOptions && newOptions.length > 0) {
          const currentSystemId = this.selectedRequest;
          const isStillValid = this.requestItems.some(item => item.id === currentSystemId);
          
          // If current selection is no longer valid, clear it
          if (!isStillValid) {
            this.selectedRequest = null;
            this.$emit('update:modelValue', null);
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    handleRequestChange(value) {
      this.selectedRequest = value;
      
      // Extract the actual name from the id
      const item = this.requestItems.find(i => i.id === value);
      const actualName = item ? item.name : value;
      
      this.$emit('update:modelValue', actualName);
      
      // Emit the full item object (with content if custom) instead of just the name
      if (item && item.isCustom) {
        // Find the full custom request with content
        const fullCustomRequest = this.customRequests.find(r => r.id === item.customId);
        this.$emit('request-changed', actualName, fullCustomRequest);
      } else {
        // System request - emit name with null object
        this.$emit('request-changed', actualName, null);
      }
      
      // Validate
      if (!value || value.trim() === '') {
        this.error = 'Data Request is required';
        this.$emit('error', this.error);
      } else {
        this.error = '';
        this.$emit('success', '');
      }
    },
    
    openCreateDialog() {
      this.isEditing = false;
      this.dialogData = {
        id: null,
        name: '',
        content: ''
      };
      this.showCreateDialog = true;
    },
    
    editRequest(request) {
      this.isEditing = true;
      const fullRequest = this.customRequests.find(r => r.id === request.customId);
      this.dialogData = {
        id: request.customId,
        name: request.name,
        content: fullRequest?.content || ''
      };
      this.showCreateDialog = true;
    },
    
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.dialogData = {
        id: null,
        name: '',
        content: ''
      };
      this.isEditing = false;
    },
    
    onDataRequestContentInput() {
      // Handle input event if needed
    },
    
    onDataRequestContentTextChange() {
      // Handle text change event if needed
    },
    
    async saveRequest() {
      if (!this.isValid) return;
      
      this.isSaving = true;
      
      // Save the editing state before closing the dialog
      const wasEditing = this.isEditing;
      
      try {
        let savedRequestId;
        
        if (this.isEditing) {
          // Update existing custom request
          const index = this.customRequests.findIndex(r => r.id === this.dialogData.id);
          if (index !== -1) {
            this.customRequests[index] = {
              id: this.dialogData.id,
              name: this.dialogData.name.trim(),
              content: this.dialogData.content
            };
            savedRequestId = this.dialogData.id;
          }
          
          // Save to localStorage for edit case
          this.saveToStorage();
          
          // Wait for next tick to ensure computed properties are updated
          await this.$nextTick();
          
          // Auto-select the edited request
          this.selectedRequest = `custom_${savedRequestId}`;
          this.handleRequestChange(this.selectedRequest);
        } else {
          // Create new custom request
          const newRequest = {
            id: Date.now().toString(),
            name: this.dialogData.name.trim(),
            content: this.dialogData.content,
            createdAt: Date.now()
          };
          this.customRequests.push(newRequest);
          
          // Save to localStorage first
          this.saveToStorage();
          
          // Wait for next tick to ensure computed properties are updated
          await this.$nextTick();
          
          // Auto-select the newly created request
          this.selectedRequest = `custom_${newRequest.id}`;
          this.handleRequestChange(this.selectedRequest);
        }
        
        this.closeCreateDialog();
        this.$emit('success', `Data request list ${wasEditing ? 'updated successfully' : 'created successfully'}`);
      } catch (error) {
        console.error('Error saving data request:', error);
        this.$emit('error', error.message || 'Failed to save data request list');
      } finally {
        this.isSaving = false;
      }
    },
    
    deleteCurrentRequest() {
      this.requestToDelete = this.customRequests.find(r => r.id === this.dialogData.id);
      this.showDeleteDialog = true;
    },
    
    async confirmDelete() {
      if (!this.requestToDelete) return;
      
      this.isDeleting = true;
      
      try {
        const index = this.customRequests.findIndex(r => r.id === this.requestToDelete.id);
        if (index !== -1) {
          this.customRequests.splice(index, 1);
        }
        
        // Save to localStorage
        this.saveToStorage();
        
        // If the deleted request was selected, clear selection
        if (this.selectedRequest === `custom_${this.requestToDelete.id}`) {
          this.selectedRequest = null;
          this.handleRequestChange(null);
        }
        
        this.showDeleteDialog = false;
        this.closeCreateDialog();
        this.$emit('success', 'Data request list deleted successfully');
      } catch (error) {
        console.error('Error deleting data request:', error);
        this.$emit('error', error.message || 'Failed to delete data request list');
      } finally {
        this.isDeleting = false;
        this.requestToDelete = null;
      }
    },
    
    loadFromStorage() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          this.customRequests = JSON.parse(stored);
        }
      } catch (error) {
        console.error('Error loading custom data requests:', error);
        this.customRequests = [];
      }
    },
    
    saveToStorage() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.customRequests));
      } catch (error) {
        console.error('Error saving custom data requests:', error);
      }
    }
  },
  mounted() {
    // Load custom requests from storage
    this.loadFromStorage();
    
    // Auto-select first option if available
    if (this.requestItems.length > 0 && !this.selectedRequest) {
      this.selectedRequest = this.requestItems[0].id;
      this.handleRequestChange(this.requestItems[0].id);
    }
  }
  }
</script>

<style scoped>
.data-request-selector {
  margin-bottom: 16px;
}

/* Ensure the select dropdown has exact height with no gaps */
.data-request-select :deep(.v-overlay__content) {
  height: 300px !important;
  max-height: 300px !important;
  min-height: 300px !important;
  overflow: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 1px solid #ccc;
}

/* Force exact container dimensions */
.data-request-select :deep(.v-menu > .v-overlay__content) {
  height: 300px !important;
  max-height: 300px !important;
}

/* Create exact container structure with no gaps */
.data-request-select :deep(.v-select__content) {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

.data-request-select :deep(.v-card) {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Style the list container with exact scrollable area */
.data-request-select :deep(.v-list) {
  padding: 0 !important;
  margin: 0 !important;
  overflow-y: auto !important;
  flex: 1 !important;
  height: calc(300px - 60px) !important;
  max-height: calc(300px - 60px) !important;
  min-height: calc(300px - 60px) !important;
}

/* Ensure list items have no gaps */
.data-request-select :deep(.v-list-item) {
  padding: 8px 16px !important;
  margin: 0 !important;
  border-bottom: 1px solid #f0f0f0;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #000;
  font-size: 16px;
}

.request-item {
  position: relative;
}

.item-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.request-item:hover .item-actions {
  opacity: 1;
}

.edit-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.add-custom-item {
  position: sticky !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 10 !important;
  gap: 32px;
  background-color: white !important;
  min-height: 50px !important;
  height: 50px !important;
  padding: 16px !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
}

/* Remove the ::after pseudo-element as it's no longer needed */
.add-custom-item::before {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  right: 0;
  height: 10px;
  background-color: white;
  z-index: 11;
}

.add-custom-text {
  color: #000000;
}

.add-custom-item:hover {
  background-color: #e8f5e8 !important;
}

/* Dropdown styles - Fix entire container to prevent text bleeding */
.data-request-select :deep(.v-list) {
  max-height: 300px;
  overflow-y: auto;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  position: relative;
}

/* Fix the overall dropdown menu container */
.data-request-select :deep(.v-menu > .v-overlay__content > .v-card) {
  overflow: hidden !important;
  max-height: 300px !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Ensure the select content area is properly contained */
.data-request-select :deep(.v-select__content) {
  max-height: 300px !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Fix the list container to prevent overflow */
.data-request-select :deep(.v-list) {
  flex: 1 1 auto !important;
  overflow-y: auto !important;
  max-height: 240px !important; /* Leave 60px for Add Custom button */
}

/* Ensure the append item area is properly positioned */
.data-request-select :deep(.v-list-group) {
  margin-bottom: 0 !important;
}

/* Ensure Add Custom button stays at bottom and fills remaining space */
.data-request-select :deep(.v-list-item--append-item) {
  position: sticky;
  bottom: 0;
  background: white !important;
  z-index: 4;
  min-height: 60px !important;
  height: 60px !important;
  padding: 16px !important;
  margin-bottom: 0 !important;
  border-top: 2px solid #e0e0e0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  display: flex !important;
  align-items: center !important;
}

/* Remove any padding/margin from the dropdown content container */
.data-request-select :deep(.v-select__content) {
  overflow: hidden !important;
  padding-bottom: 0 !important;
  max-height: 300px !important;
  display: flex !important;
  flex-direction: column !important;
}

.data-request-select :deep(.v-select__content .v-list) {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  flex: 1 1 auto !important;
  max-height: 240px !important;
}

/* Ensure the dropdown menu itself has no bottom spacing and proper height */
.data-request-select :deep(.v-overlay__content) {
  padding-bottom: 0 !important;
  max-height: 300px !important;
  overflow: hidden !important;
}

/* Fix the Vuetify menu wrapper */
.data-request-select :deep(.v-menu > .v-overlay__content) {
  overflow: hidden !important;
  max-height: 300px !important;
}

/* Ensure proper container for the entire select */
.data-request-select :deep(.v-field) {
  position: relative;
}

/* Fix any default Vuetify card padding in dropdown */
.data-request-select :deep(.v-card) {
  padding: 0 !important;
  margin: 0 !important;
  max-height: 300px !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Remove all default padding from v-select components */
.data-request-select :deep(.v-select .v-menu) {
  padding: 0 !important;
}

.data-request-select :deep(.v-menu .v-list) {
  padding: 0 !important;
  margin: 0 !important;
}

/* Target the specific menu content wrapper */
.data-request-select :deep(.v-menu__content) {
  padding: 0 !important;
  margin: 0 !important;
  max-height: 300px !important;
  overflow: hidden !important;
}

/* Remove padding from overlay scrim */
.data-request-select :deep(.v-overlay__scrim) {
  padding: 0 !important;
}

/* Ensure list group has no spacing */
.data-request-select :deep(.v-list-group__items) {
  padding: 0 !important;
}

/* Remove any theme-specific padding */
.data-request-select :deep(.v-theme--light) {
  padding: 0 !important;
}

/* Force the entire dropdown menu to have no spacing */
.data-request-select :deep(*) {
  box-sizing: border-box;
}

.data-request-select :deep(.v-menu),
.data-request-select :deep(.v-overlay),
.data-request-select :deep(.v-overlay__content),
.data-request-select :deep(.v-card),
.data-request-select :deep(.v-list),
.data-request-select :deep(.v-select__content) {
  padding: 0 !important;
  margin: 0 !important;
}

/* Ensure list items don't bleed through */
.data-request-select :deep(.v-list-item) {
  position: relative;
  z-index: 1;
}

/* Force remove any default Vuetify spacing */
.data-request-select :deep(.v-list),
.data-request-select :deep(.v-list-item-group),
.data-request-select :deep(.v-menu__content),
.data-request-select :deep(.v-select__content) {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

/* Ensure the entire dropdown area has no gaps */
.data-request-select :deep(.v-field__input) {
  padding-bottom: 0 !important;
}

::v-deep .v-list-item__prepend {
  width: 0px !important;
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', sans-serif;
}

.data-request-selector :deep(.v-select) {
  font-size: 14px;
}

.data-request-selector :deep(.v-select .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 16px;
  line-height: 48px;
  text-align: left;
}

.data-request-selector :deep(.v-field) {
  border: 1px solid #5A5A5A !important;
  border-radius: 4px !important;
}

.data-request-selector :deep(.v-field--focused) {
  border: 1px solid #5A5A5A !important;
  box-shadow: none !important;
}

.data-request-selector :deep(.v-field--error) {
  border: 1px solid #f44336 !important;
}

.data-request-selector :deep(.v-field__outline) {
  display: none !important;
}

/* Dialog styles */
.dialog-field-container {
  margin-bottom: 1rem;
}

.dialog-field-label {
  display: block;
  margin-bottom: 8px;
  color: #000;
  font-size: 14px;
  font-weight: 500;
}

.field-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  text-align: right;
}

.field-hint.error {
  color: #d32f2f;
  font-weight: 500;
}

.delete-section {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.delete-icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  object-fit: contain;
}

.delete {
  padding: 4px 8px 4px 8px;
  text-transform: capitalize;
}

/* Rich Text Editor Styles */
.data-request-content-container {
  position: relative;
}

.data-request-content-vue-editor {
  background: #ffffff;
  border: 1px solid #5A5A5A;
  border-radius: 4px;
}

.data-request-content-vue-editor :deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid #e0e0e0;
  background: #f5f5f5;
  border-radius: 4px 4px 0 0;
}

.data-request-content-vue-editor :deep(.ql-container) {
  border: none;
  min-height: 150px;
  max-height: 300px;
  overflow-y: auto;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', sans-serif;
}

.data-request-content-vue-editor :deep(.ql-editor) {
  min-height: 150px;
  padding: 12px;
  font-size: 14px;
  color: #000;
}

.data-request-content-vue-editor :deep(.ql-editor.ql-blank::before) {
  color: #757575;
  font-style: normal;
  font-size: 14px;
}

.data-request-content-char-count {
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.data-request-content-char-count.error {
  color: #d32f2f;
  font-weight: 500;
}
</style>